import _React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoginForm } from '@shared/components/auth/LoginForm';
import { RegistrationForm } from '@shared/components/auth/RegistrationForm';
import { ForgotPasswordForm } from '@shared/components/auth/ForgotPasswordForm';
import { OtpChallengeForm } from '@shared/components/auth/OtpChallengeForm';
import { OtpEnrollmentForm } from '@shared/components/auth/OtpEnrollmentForm';
import { PartnerLoginPage } from '@shared/components/auth/PartnerLoginPage';
import { useAuthStore } from '@features/auth/hooks/useAuthStore';

type AuthView = 'login' | 'register' | 'forgot' | 'enroll';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partnerParam = searchParams.get("partner");
  const isPartnerMode = !!partnerParam;
  const [view, setView] = useState<AuthView>('login');
  const [enrollEmail, setEnrollEmail] = useState('');
  // A pending TOTP challenge takes over the form area regardless of `view`.
  const pendingOtp = useAuthStore((s) => s.pendingOtp);

  const enterApp = () => navigate('/', { replace: true });

  // Partner login layout
  if (isPartnerMode) {
    return <PartnerLoginPage partnerParam={partnerParam!} />;
  }

  // Regular login layout — split-screen (shadcn login-02 style)
  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      {/* Left column: brand + form */}
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <a href="/" className="flex items-center font-medium" aria-label="Vinr home">
            <img src="/images/vinr-dark.svg" alt="Vinr" className="h-8 dark:hidden" />
            <img src="/images/vinr-light.svg" alt="Vinr" className="hidden h-8 dark:block" />
          </a>
        </div>

        <div className="flex flex-1 items-center justify-center">
          <div
            key={pendingOtp ? 'otp' : view}
            className="w-full max-w-md animate-fade-in"
          >
            {pendingOtp ? (
              <OtpChallengeForm onCancel={() => setView('login')} />
            ) : view === 'enroll' ? (
              <OtpEnrollmentForm
                email={enrollEmail}
                onEnrolled={enterApp}
                onSkip={enterApp}
              />
            ) : (
              <>
                {view === 'register' && (
                  <RegistrationForm
                    onShowLogin={() => setView('login')}
                    onRegistered={(email) => {
                      setEnrollEmail(email);
                      setView('enroll');
                    }}
                  />
                )}
                {view === 'forgot' && (
                  <ForgotPasswordForm onShowLogin={() => setView('login')} />
                )}
                {view === 'login' && (
                  <LoginForm
                    onShowRegister={() => setView('register')}
                    onShowForgot={() => setView('forgot')}
                  />
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground md:justify-start">
          <span>© Vinr</span>
          <a href="#" className="hover:text-foreground transition-colors">
            Privacy &amp; terms
          </a>
        </div>
      </div>

      {/* Right column: branded panel */}
      <div className="relative hidden bg-primary lg:flex lg:flex-col lg:justify-center lg:gap-6 lg:p-12">
        <div className="flex flex-col gap-4">
          <h2 className="text-3xl font-semibold text-balance text-primary-foreground">
            Payments infrastructure for modern businesses
          </h2>
          <p className="max-w-md text-pretty leading-relaxed text-primary-foreground opacity-90">
            Manage merchants, channels, and settlements from a single platform built
            for scale, speed, and compliance.
          </p>
        </div>
        <p className="text-sm text-primary-foreground opacity-80">
          Trusted by leading banks and merchants worldwide.
        </p>
      </div>
    </div>
  );
}
